export interface ReportArea {
    country: string;
    subdivisionCodes: string[];
}

// ISO 3166-2 identifiers. Keep the supported catalogue shared by UI and server.
const catalogue: Record<string, { name: string; prefix: string; regions: string }> = {
    USA: { name: 'United States', prefix: 'US', regions: 'AL:Alabama|AK:Alaska|AZ:Arizona|AR:Arkansas|CA:California|CO:Colorado|CT:Connecticut|DE:Delaware|DC:District of Columbia|FL:Florida|GA:Georgia|HI:Hawaii|ID:Idaho|IL:Illinois|IN:Indiana|IA:Iowa|KS:Kansas|KY:Kentucky|LA:Louisiana|ME:Maine|MD:Maryland|MA:Massachusetts|MI:Michigan|MN:Minnesota|MS:Mississippi|MO:Missouri|MT:Montana|NE:Nebraska|NV:Nevada|NH:New Hampshire|NJ:New Jersey|NM:New Mexico|NY:New York|NC:North Carolina|ND:North Dakota|OH:Ohio|OK:Oklahoma|OR:Oregon|PA:Pennsylvania|RI:Rhode Island|SC:South Carolina|SD:South Dakota|TN:Tennessee|TX:Texas|UT:Utah|VT:Vermont|VA:Virginia|WA:Washington|WV:West Virginia|WI:Wisconsin|WY:Wyoming' },
    CAN: { name: 'Canada', prefix: 'CA', regions: 'AB:Alberta|BC:British Columbia|MB:Manitoba|NB:New Brunswick|NL:Newfoundland and Labrador|NS:Nova Scotia|NT:Northwest Territories|NU:Nunavut|ON:Ontario|PE:Prince Edward Island|QC:Quebec|SK:Saskatchewan|YT:Yukon' },
    AUS: { name: 'Australia', prefix: 'AU', regions: 'ACT:Australian Capital Territory|NSW:New South Wales|NT:Northern Territory|QLD:Queensland|SA:South Australia|TAS:Tasmania|VIC:Victoria|WA:Western Australia' },
};

export const REPORT_COUNTRIES = Object.entries(catalogue).map(([code, value]) => ({ code, name: value.name }));
const subdivisions = Object.fromEntries(Object.entries(catalogue).map(([country, value]) => [country,
    value.regions.split('|').map(region => {
        const [code, name] = region.split(':');
        return { code: `${value.prefix}-${code}`, name };
    }).sort((a, b) => a.name.localeCompare(b.name)),
]));

export function getSubdivisions(country: string): { code: string; name: string }[] {
    return subdivisions[country] || [];
}

export function validateReportArea(value: unknown): ReportArea {
    const area = value as ReportArea;
    if (!area || typeof area.country !== 'string' || !/^[A-Z]{3}$/.test(area.country) || !Array.isArray(area.subdivisionCodes))
        throw new Error('Choose a country and valid states or provinces.');
    const allowed = new Set(getSubdivisions(area.country).map(region => region.code));
    if (area.subdivisionCodes.length > 60 || area.subdivisionCodes.some(code => typeof code !== 'string' || !allowed.has(code)))
        throw new Error('Selected states must belong to the chosen country.');
    return { country: area.country, subdivisionCodes: [...new Set(area.subdivisionCodes)].sort() };
}

export function reportAreaLabel(area: ReportArea): string {
    const countryName = catalogue[area.country]?.name || area.country;
    const names = getSubdivisions(area.country).filter(region => area.subdivisionCodes.includes(region.code)).map(region => region.name);
    return names.length ? `${names.join(', ')} · ${countryName}` : `${countryName} · Whole country`;
}

/** Match explicit source regions only; city names and country centroids prove no state. */
export function eventSubdivision(event: any, country: string): string | null {
    if ((event.iso3 || event.geo?.country_iso3 || event.location?.country) !== country)
        return null;
    const regions = getSubdivisions(country);
    const explicit = event.subdivisionCode ?? event.geo?.subdivision_code;
    if (explicit != null)
        return regions.some(region => region.code === explicit) ? explicit : null;
    const parts = String(event.location_name || event.geo?.location_name || '').split(',').map(part => part.trim().toLowerCase());
    // Source locations must explicitly contain city, state, country in that order.
    const countryNames = [catalogue[country]?.name.toLowerCase(), country.toLowerCase()];
    if (country === 'USA')
        countryNames.push('united states of america');
    if (parts.length < 3 || !countryNames.includes(parts[parts.length - 1]))
        return null;
    const state = parts[parts.length - 2];
    return regions.find(region => region.name.toLowerCase() === state)?.code || null;
}

export function timestampMillis(value: any): number | null {
    const millis = typeof value?.toMillis === 'function' ? value.toMillis()
        : value && typeof value.seconds === 'number' ? value.seconds * 1000
            : typeof value === 'number' ? value : typeof value === 'string' ? Date.parse(value) : NaN;
    return Number.isFinite(millis) ? millis : null;
}

export function buildDailyAreaReport(areaInput: ReportArea, events: any[], national: any, now = Date.now()) {
    const area = validateReportArea(areaInput);
    const selected = new Set(area.subdivisionCodes);
    const cutoff = now - 86_400_000;
    const localEvents: any[] = [];
    let unknownLocationCount = 0;
    let unknownTimeCount = 0;
    let reviewedCount = 0;
    const seen = new Set<string>();
    const clip = (value: unknown, length: number) => {
        const text = String(value || '');
        return text.length > length ? `${text.slice(0, length)}…` : text;
    };
    for (let i = 0; i < events.length; i++) {
        const event = events[i];
        if ((event.iso3 || event.geo?.country_iso3) !== area.country || event.vettingStatus !== 'approved' || event.verified !== true)
            continue;
        const received = timestampMillis(event.processedAt);
        if (received === null || received < cutoff || received > now)
            continue;
        const published = timestampMillis(event.publishedAt ?? event.pubDate);
        if (published === null) {
            unknownTimeCount++;
            continue;
        }
        if (published < cutoff || published > now)
            continue;
        const identity = String(event.source_url || event.id);
        if (seen.has(identity))
            continue;
        seen.add(identity);
        reviewedCount++;
        const subdivisionCode = eventSubdivision(event, area.country);
        if (!subdivisionCode) {
            unknownLocationCount++;
            continue;
        }
        if (!selected.has(subdivisionCode))
            continue;
        localEvents.push({
            id: String(event.id), title: clip(event.title || 'Security event', 300),
            summary: clip(event.summary, 2000), subdivisionCode,
            location: clip(event.location_name || event.geo?.location_name, 300),
            publishedAt: new Date(published).toISOString(),
            lat: Number.isFinite(Number(event.lat ?? event.geo?.lat)) ? Number(event.lat ?? event.geo?.lat) : null,
            lng: Number.isFinite(Number(event.lng ?? event.geo?.lng)) ? Number(event.lng ?? event.geo?.lng) : null,
            category: clip(event.category, 100),
            riskScore: event.riskScore != null && String(event.riskScore).trim() !== '' && Number.isFinite(Number(event.riskScore))
                ? Math.min(100, Math.max(0, Number(event.riskScore))) : null,
            confidencePercent: event.confidencePercent ?? event.confidence?.percentage ?? null,
            confidence_percentage: event.confidence_percentage ?? null,
            confidenceScore: event.confidenceScore ?? null,
            confidence: event.confidence ?? null,
            vettingMethod: event.vettingMethod ?? null,
            vettingVersion: event.vettingVersion ?? null,
            verified: true,
            vettingStatus: 'approved',
        });
    }
    localEvents.sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
    const matchedCount = localEvents.length;
    const omittedEventCount = Math.max(0, matchedCount - 100);
    localEvents.length = Math.min(matchedCount, 100);
    const baseline = national?.fullReportData || national || {};
    const nationalSummary = baseline.executiveSummary || baseline.summary || baseline.riskSummary || national?.latestIncidentSummary;
    return {
        reportType: 'DailyMicroReport', reportArea: area,
        title: `Daily Micro: ${reportAreaLabel(area)}`, location: reportAreaLabel(area),
        country: catalogue[area.country]?.name || area.country,
        generatedAt: new Date(now).toISOString(), windowStart: new Date(cutoff).toISOString(),
        summary: matchedCount ? `${matchedCount} verified events matched your selected area in the last 24 hours.${omittedEventCount ? ' The latest 100 are shown.' : ''}` : 'No matching events found in the last 24 hours. This does not mean there is no risk.',
        localEvents, coverage: { reviewedCount, unknownLocationCount, unknownTimeCount, omittedEventCount },
        nationalContext: {
            summary: typeof nationalSummary === 'string' ? clip(nationalSummary, 16000) : 'National context is unavailable.',
            updatedAt: timestampMillis(national?.lastUpdated) === null ? null : new Date(timestampMillis(national.lastUpdated)!).toISOString(),
        },
    };
}

export type DailyAreaReport = ReturnType<typeof buildDailyAreaReport>;
